// Shared helpers and constants for the new Mentor pages.

export const DEFAULTS = {
  price: 249,
  duration: '45–60 min',
  sessionMode: 'Video call on MentoMEET',
  replyRate: '98%',
  responseTime: '< 2 hrs',
  guarantee: '7-day money-back guarantee',
  payment: 'Secure payment via Razorpay',
  features: [
    { icon: '⏱', text: '45–60 min session' },
    { icon: '📋', text: 'Session notes shared after' },
    { icon: '💬', text: 'Pre-session chat to prepare' },
    { icon: '📹', text: 'Video call on MentoMEET' },
  ],
};

export const CATEGORY_META = {
  JEE: {
    label: 'JEE',
    full: 'JEE Main / Advanced',
    badgeClass: 'mm-badge-jee',
    icon: '⚗️',
    topics: [
      { name: 'How to analyse your JEE mock tests', tag: 'Deep analysis', tagClass: 'mm-pill-analysis' },
      { name: 'Physics/Chemistry/Maths priority setting', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: 'Book & resource selection — what to read and skip', tag: 'Strategy', tagClass: 'mm-pill-strat' },
      { name: 'Subject-wise marks improvement roadmap', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: '3/6 month revision calendar tailored to your exam', tag: 'Strategy', tagClass: 'mm-pill-strat' },
    ],
    expertiseDesc: (subj) => `JEE-level ${subj} — from fundamentals to advanced problem-solving and exam strategy.`,
  },
  NEET: {
    label: 'NEET',
    full: 'NEET UG',
    badgeClass: 'mm-badge-neet',
    icon: '🧬',
    topics: [
      { name: 'How to analyse your NEET mock test scores', tag: 'Deep analysis', tagClass: 'mm-pill-analysis' },
      { name: 'Revision strategy for Biology & Zoology', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: 'Book & resource selection — what to read and what to skip', tag: 'Strategy', tagClass: 'mm-pill-strat' },
      { name: 'Subject-wise marks improvement roadmap', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: '3/6 month revision calendar tailored to your exam date', tag: 'Strategy', tagClass: 'mm-pill-strat' },
    ],
    expertiseDesc: (subj) => `NEET UG ${subj} — from basics to high-weightage application and mock analysis.`,
  },
  CAREER: {
    label: 'CAREER',
    full: 'Career & Placement',
    badgeClass: 'mm-badge-career',
    icon: '💼',
    topics: [
      { name: 'Resume and LinkedIn review for your target role', tag: 'Deep analysis', tagClass: 'mm-pill-analysis' },
      { name: 'Internship / placement roadmap', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: 'Interview preparation and DSA/HR round strategy', tag: 'Strategy', tagClass: 'mm-pill-strat' },
      { name: 'Company and role shortlist for your profile', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: 'Switching jobs or picking the right offer', tag: 'Strategy', tagClass: 'mm-pill-strat' },
    ],
    expertiseDesc: (subj) => `Career mentorship in ${subj} — placement strategy, interviews and profile building.`,
  },
  DEVELOPMENT: {
    label: 'DEVELOPMENT',
    full: 'Development / Web',
    badgeClass: 'mm-badge-board',
    icon: '💻',
    topics: [
      { name: 'Project roadmap and tech stack selection', tag: 'Planning', tagClass: 'mm-pill-plan' },
      { name: 'Coding best practices and code review', tag: 'Deep analysis', tagClass: 'mm-pill-analysis' },
      { name: 'Portfolio and resume for developer roles', tag: 'Strategy', tagClass: 'mm-pill-strat' },
      { name: 'Interview prep for frontend / backend roles', tag: 'Strategy', tagClass: 'mm-pill-strat' },
    ],
    expertiseDesc: (subj) => `Practical ${subj} guidance — projects, tools and industry-ready skills.`,
  },
};

export const COLLEGE_TYPE_ORDER = ['IIT', 'AIIMS', 'NIT', 'IIIT', 'OTHER'];

export const GRADIENTS = [
  'linear-gradient(135deg,#7B2FBE,#9B4FDE)',
  'linear-gradient(135deg,#5A1F8C,#7B2FBE)',
  'linear-gradient(135deg,#065F46,#10B981)',
  'linear-gradient(135deg,#B86000,#F59E0B)',
  'linear-gradient(135deg,#1D4ED8,#60A5FA)',
  'linear-gradient(135deg,#7C3AED,#A855F7)',
  'linear-gradient(135deg,#BE185D,#F472B6)',
  'linear-gradient(135deg,#0F172A,#475569)',
];

export function toList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') return value.split(/[,;]/).map(s => s.trim()).filter(Boolean);
  return [String(value)];
}

export function getInitials(firstName, lastName) {
  return ((firstName || '')[0] || '') + ((lastName || '')[0] || '');
}

export function stringHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function seededRating(id) {
  if (!id) return '4.5';
  const h = stringHash(String(id));
  const v = 4.0 + (h % 11) / 10; // 4.0 to 5.0 in 0.1 steps
  return v.toFixed(1);
}

export function gradientFromId(id) {
  return GRADIENTS[stringHash(String(id)) % GRADIENTS.length];
}

export function joinedDate(id) {
  if (!id) return '';
  const ts = parseInt(String(id).slice(0, 8), 16) * 1000;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

export function safeUrl(url) {
  if (!url) return '';
  const s = String(url).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/\//i.test(s)) return 'https:' + s;
  return '';
}

export function getCategory(user) {
  const cat = user?.category || user?.history?.[0]?.category || '';
  return String(cat).toUpperCase();
}

export function getMentorView(user) {
  const h = user?.history?.[0] || {};
  const category = getCategory(user);
  const catMeta = CATEGORY_META[category] || CATEGORY_META.JEE;
  const expertise = toList(h.expertise);
  const language = toList(h.language);
  const rank = h.rank ? String(h.rank) : '';

  return {
    _id: user?._id,
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Mentor',
    initials: getInitials(user?.firstName, user?.lastName),
    photo: h.profile_picture || '',
    gradient: gradientFromId(user?._id),
    email: user?.email || '',
    role: user?.role || 'Mentor',
    category,
    catMeta,
    isFilled: !!h.college,
    college: h.college || '',
    collegeType: h.college_type || 'OTHER',
    branch: h.branch || '',
    year: h.year ? String(h.year) : '',
    rank,
    expertise,
    subject: expertise.slice(0, 2).join(' · ') || catMeta.label,
    language,
    languagesText: language.slice(0, 2).join(' & ') || 'English',
    aboutMe: h.about_me || '',
    fbLink: safeUrl(h.fb_link),
    linkedinLink: safeUrl(h.linkedin_link),
    company: h.company || '',
    ppo: h.start_time || '',
    joined: joinedDate(user?._id),
    price: DEFAULTS.price,
    rating: seededRating(user?._id),
  };
}

export function getMenteeView(user) {
  const h = user?.history?.[0] || {};
  const category = getCategory(user);
  return {
    _id: user?._id,
    fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Student',
    initials: getInitials(user?.firstName, user?.lastName),
    photo: h.profile_picture || '',
    gradient: gradientFromId(user?._id),
    email: user?.email || '',
    role: user?.role || 'Mentee',
    category,
    isFilled: !!h.standard,
    standard: h.standard || '',
    coaching: h.coaching || '',
    subject: h.subject || '',
    joined: joinedDate(user?._id),
  };
}

export function goToBooking(e) {
  if (e) e.stopPropagation();
  if (!localStorage.getItem('token')) {
    window.location.href = '/login';
    return;
  }
  window.location.href = '/scheduleMeeting';
}

export function getDomainCounts(mentors) {
  const counts = { ALL: mentors.length };
  mentors.forEach(m => {
    const c = getCategory(m);
    counts[c] = (counts[c] || 0) + 1;
  });
  return counts;
}

export function getCollegeTypeCounts(mentors) {
  const counts = {};
  mentors.forEach(m => {
    const t = (m.history?.[0]?.college_type || 'OTHER');
    counts[t] = (counts[t] || 0) + 1;
  });
  return counts;
}
